/**
 * Product Categories Dual-Write Test Suite
 *
 * This utility automatically creates product categories with various configurations
 * and verifies that Supabase and Firestore are in sync.
 *
 * Usage:
 *   import { runProductCategoriesDualWriteTests } from './utils/productCategoriesDualWriteTest';
 *   await runProductCategoriesDualWriteTests(db, testConfig);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import {
  saveNewProductCategory,
  updateProductCategory,
  deleteProductCategory,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { productCategoriesApi } from '../global/API/typedApiClient';
import { getDefaultStore } from 'jotai';
import {
  organizationAtom,
  dbAtom,
  userIdAtom,
  productCategoriesDictAtom,
  storesDictAtom,
} from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * All test variations to run
 */
export const CATEGORY_TEST_VARIATIONS = [
  // Basic CRUD (PC1-PC2)
  { id: 'PC1', category: 'basic', name: 'Create with required fields only' },
  { id: 'PC2', category: 'basic', name: 'Create with color' },

  // Update (PC3)
  { id: 'PC3', category: 'lifecycle', name: 'Update category name', lifecycleAction: 'update' },

  // Delete (PC4)
  { id: 'PC4', category: 'lifecycle', name: 'Delete category', lifecycleAction: 'delete' },
];

// Alias for consistency with other test files
export const PRODUCT_CATEGORY_TEST_VARIATIONS = CATEGORY_TEST_VARIATIONS;

/**
 * Generate test category data for a specific variation
 */
export function generateTestCategory(variation, testData) {
  const { organizationId, storeId } = testData;

  // Base category data
  const now = new Date();
  const timestamp = Date.now();

  const baseCategory = {
    organizationId,
    name: `Test Category ${variation.id} - ${timestamp}`,
    color: null,
    parentID: null,
    lastChangeAt: now,
    lastChangeStore: storeId,
    dailyStatID: `test-${formatDateForDailyStat(now)}-${storeId}`,
  };

  // Apply variation
  switch (variation.category) {
    case 'basic':
      return applyBasicVariation(baseCategory, variation, testData);
    case 'lifecycle':
      return applyLifecycleVariation(baseCategory, variation, testData);
    default:
      return baseCategory;
  }
}

function applyBasicVariation(category, variation, testData) {
  switch (variation.id) {
    case 'PC1': // Required fields only
      category.name = `Test Basic Category ${Date.now()}`;
      break;

    case 'PC2': // With color
      category.name = `Test Colored Category ${Date.now()}`;
      category.color = '#FF5733';
      break;
  }

  return category;
}

function applyLifecycleVariation(category, variation, testData) {
  // Start with a basic category that can be modified
  category.name = `Test Lifecycle Category ${variation.id} - ${Date.now()}`;

  // Store lifecycle action metadata
  category._lifecycleAction = variation.lifecycleAction;
  category._lifecycleData = {};

  switch (variation.id) {
    case 'PC3': // Update
      category._lifecycleData = {
        updateFields: true,
        updateInfo: {
          name: `Updated Category Name ${Date.now()}`,
          color: '#00FF00',
        },
      };
      break;

    case 'PC4': // Delete
      category._lifecycleData = {
        deleteCategory: true,
      };
      break;
  }

  return category;
}

/**
 * Format date for dailyStatID (d-M-y format)
 */
function formatDateForDailyStat(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

/**
 * Execute lifecycle action after category creation
 */
async function executeLifecycleAction(db, categoryRef, categoryData, testData) {
  const action = categoryData._lifecycleAction;
  const data = categoryData._lifecycleData;

  if (!action || !data) return { actionPerformed: null };

  // Handle skip case
  if (data.skip) {
    return { actionPerformed: 'skip', skipReason: data.skipReason };
  }

  // Wait a bit for initial sync
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    if (data.updateFields) {
      // Update category via updateProductCategory
      await updateProductCategory(categoryRef, data.updateInfo);
      return { actionPerformed: 'updateFields' };
    }

    if (data.deleteCategory) {
      const jotaiStore = getDefaultStore();
      const userId = jotaiStore.get(userIdAtom) || 'test-user';
      // Delete with empty products array (no products to move)
      await deleteProductCategory(categoryRef, [], null, userId);
      return { actionPerformed: 'delete' };
    }
  } catch (error) {
    console.error('Lifecycle action failed:', error.message);
    return { actionPerformed: action, error: error.message };
  }

  return { actionPerformed: null };
}

/**
 * Run a single test variation
 */
async function runSingleTest(db, variation, testData, options = {}) {
  const { cleanup = true, delayMs = 5000 } = options;

  console.log(`\n--- Running test ${variation.id}: ${variation.name} ---`);

  try {
    // Generate test category
    const categoryData = generateTestCategory(variation, testData);

    // Check if test should be skipped
    if (categoryData._lifecycleData?.skip) {
      console.log(`SKIP: ${variation.name} - ${categoryData._lifecycleData.skipReason}`);
      testResults.skipped.push({ variation, reason: categoryData._lifecycleData.skipReason });
      return null;
    }

    console.log(`Creating category...`);

    // Create category via frontend code (this triggers dual-write)
    const result = await saveNewProductCategory(db, categoryData);
    const firestoreId = result.id;
    const categoryRef = db.collection('productCategories').doc(firestoreId);

    console.log(`Created category: ${firestoreId}`);

    // Wait for Supabase write to complete (CRITICAL: 5 second wait)
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // For lifecycle tests, perform the action after initial creation
    let lifecycleResult = null;
    if (variation.category === 'lifecycle') {
      console.log(`Performing lifecycle action: ${variation.lifecycleAction}`);
      lifecycleResult = await executeLifecycleAction(db, categoryRef, categoryData, testData);
      console.log(`Lifecycle action result:`, lifecycleResult);

      // Handle skipped lifecycle tests
      if (lifecycleResult?.actionPerformed === 'skip') {
        console.log(`SKIP: ${variation.name} - ${lifecycleResult.skipReason}`);
        testResults.skipped.push({ variation, reason: lifecycleResult.skipReason });
        // Cleanup the created category
        if (cleanup) {
          try {
            const jotaiStore = getDefaultStore();
            const userId = jotaiStore.get(userIdAtom) || 'test-user';
            await deleteProductCategory(categoryRef, [], null, userId);
          } catch (e) { /* ignore */ }
        }
        return null;
      }

      // Wait for the update to sync (CRITICAL: 5 second wait)
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    // Compare using the API
    let comparisonResult;

    // Special case: if category was deleted, verify it's gone from both DBs
    if (lifecycleResult?.actionPerformed === 'delete') {
      comparisonResult = await verifyDeletion(firestoreId, categoryRef, db);
    } else {
      comparisonResult = await compareViaApi(firestoreId, categoryRef, db, categoryData);
    }

    // Log result
    if (comparisonResult.matches) {
      console.log(`PASS: ${variation.name}`);
      testResults.passed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    } else {
      console.log(`FAIL: ${variation.name}`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparisonResult.differences), null, 2));
      testResults.failed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    }

    // Cleanup - delete the test category (skip if already deleted)
    if (cleanup && lifecycleResult?.actionPerformed !== 'delete') {
      try {
        const categoryDoc = await categoryRef.get();
        if (categoryDoc.exists) {
          const jotaiStore = getDefaultStore();
          const userId = jotaiStore.get(userIdAtom) || 'test-user';
          await deleteProductCategory(categoryRef, [], null, userId);
          console.log(`Cleaned up category: ${firestoreId}`);
        }
      } catch (cleanupError) {
        console.warn(`Failed to cleanup category ${firestoreId}:`, cleanupError.message);
      }
    }

    return comparisonResult;
  } catch (error) {
    console.error(`ERROR in test ${variation.id}:`, error.message);
    console.error(`Stack trace:`, error.stack);
    testResults.failed.push({
      variation,
      error: error.message,
      stack: error.stack,
    });
    return null;
  }
}

/**
 * Compare category data via backend API
 */
async function compareViaApi(firestoreId, categoryRef, db, originalCategoryData) {
  try {
    const response = await api.get(`/product-categories/compare/${firestoreId}`);
    const apiResult = response.data;

    // Convert API result to our comparison format
    if (!apiResult.exists) {
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: true,
        matches: false,
        differences: [{
          field: 'category',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    // Get Firestore data for comparison
    let firestoreData = null;
    try {
      const doc = await categoryRef.get();
      if (doc.exists) {
        firestoreData = { id: doc.id, ...doc.data() };
      }
    } catch (error) {
      console.warn(`Could not fetch from Firestore: ${error.message}`);
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare fields
    const fieldsToCompare = [
      { supabase: 'name', firestore: 'name' },
    ];

    for (const field of fieldsToCompare) {
      let supabaseValue = supabaseData?.[field.supabase];
      let firestoreValue = firestoreData?.[field.firestore];

      // Normalize values
      supabaseValue = normalizeValue(supabaseValue);
      firestoreValue = normalizeValue(firestoreValue);

      if (!valuesMatch(supabaseValue, firestoreValue)) {
        differences.push({
          field: field.supabase,
          supabaseValue,
          firestoreValue,
          severity: 'critical',
        });
      }
    }

    return {
      firestoreId,
      foundInSupabase: true,
      foundInFirestore: !!firestoreData,
      matches: differences.length === 0,
      differences,
      warnings: [],
      supabaseData,
      firestoreData,
    };
  } catch (error) {
    console.warn('API comparison failed:', error.message);
    return {
      firestoreId,
      foundInSupabase: false,
      foundInFirestore: true,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: error.message,
        firestoreValue: 'exists',
        severity: 'critical',
      }],
      warnings: [error.message],
    };
  }
}

/**
 * Verify that a category has been deleted from both databases
 */
async function verifyDeletion(firestoreId, categoryRef, db) {
  const maxRetries = 15;
  const retryDelayMs = 2000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const differences = [];
    let firestoreExists = false;
    let supabaseExists = false;

    // Check Firestore
    try {
      const firestoreDoc = await categoryRef.get();
      firestoreExists = firestoreDoc.exists;
    } catch (error) {
      firestoreExists = false;
    }

    // Check Supabase via compare endpoint
    try {
      const response = await api.get(`/product-categories/compare/${firestoreId}`);
      supabaseExists = response.data?.exists || false;
    } catch (error) {
      // 404 or other error means not found
      supabaseExists = false;
    }

    // Both deleted - success!
    if (!firestoreExists && !supabaseExists) {
      console.log(`Delete verified on attempt ${attempt}: both databases show deleted`);
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: false,
        matches: true,
        differences: [],
        warnings: [],
      };
    }

    // Build differences for logging
    if (firestoreExists) {
      differences.push({
        field: 'firestore_deletion',
        supabaseValue: 'deleted',
        firestoreValue: 'still exists',
        severity: 'critical',
      });
    }
    if (supabaseExists) {
      differences.push({
        field: 'supabase_deletion',
        supabaseValue: 'still exists',
        firestoreValue: 'deleted',
        severity: 'critical',
      });
    }

    // If last attempt, return failure
    if (attempt === maxRetries) {
      console.log(`Delete verification failed after ${maxRetries} attempts`);
      return {
        firestoreId,
        foundInSupabase: supabaseExists,
        foundInFirestore: firestoreExists,
        matches: false,
        differences,
        warnings: [`Deletion not complete after ${maxRetries} retries`],
      };
    }

    // Wait and retry
    console.log(`Delete verification attempt ${attempt}/${maxRetries}: waiting...`);
    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
  }

  return {
    firestoreId,
    foundInSupabase: true,
    foundInFirestore: true,
    matches: false,
    differences: [],
    warnings: ['Unexpected state in verifyDeletion'],
  };
}

function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  if (value === '') return null;
  if (typeof value === 'number') return Number(value);
  if (typeof value === 'boolean') return value;
  return value;
}

function valuesMatch(a, b) {
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.01;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
}

/**
 * Run all test variations
 */
export async function runProductCategoriesDualWriteTests(passedDb, options = {}) {
  const {
    variations = CATEGORY_TEST_VARIATIONS,
    cleanup = true,
    delayMs = 5000,
    stopOnFailure = false,
  } = options;

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const userId = jotaiStore.get(userIdAtom);

  // Get db from atom if not passed
  const db = passedDb || jotaiStore.get(dbAtom);

  if (!db) {
    throw new Error('Database not initialized. Please wait for app to fully load.');
  }

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!storesDict || Object.keys(storesDict).length === 0) {
    throw new Error('Stores not loaded. Please wait for app to fully load.');
  }

  // Get first store
  const storeId = Object.keys(storesDict)[0];
  if (!storeId) {
    throw new Error('No stores found. Please create a store first.');
  }

  const store = storesDict[storeId];

  const testData = {
    organizationId: organization.id,
    storeId,
    store,
    stores: storesDict,
    uid: userId || 'test-uid',
  };

  console.log('\n========================================');
  console.log('PRODUCT CATEGORIES DUAL-WRITE TEST SUITE');
  console.log('========================================');
  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Store: ${store.name || storeId}`);
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    const result = await runSingleTest(db, variation, testData, { cleanup, delayMs });

    if (stopOnFailure && result && !result.matches) {
      console.log('\nStopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Print summary
  console.log('\n========================================');
  console.log('TEST SUMMARY');
  console.log('========================================');
  console.log(`Passed: ${testResults.passed.length}`);
  console.log(`Failed: ${testResults.failed.length}`);
  console.log(`Skipped: ${testResults.skipped.length}`);
  console.log('========================================\n');

  if (testResults.failed.length > 0) {
    console.log('FAILED TESTS:');
    testResults.failed.forEach(({ variation, result, error }) => {
      console.log(`  - ${variation.id}: ${variation.name}`);
      if (error) {
        console.log(`    Error: ${error}`);
      } else if (result?.differences) {
        result.differences.forEach(diff => {
          console.log(`    ${diff.field}: Supabase=${JSON.stringify(sanitizeForFirestore(diff.supabaseValue))} vs Firestore=${JSON.stringify(sanitizeForFirestore(diff.firestoreValue))}`);
        });
      }
    });
  }

  return testResults;
}

/**
 * Quick smoke test - runs only the first 2 basic tests
 */
export async function runQuickProductCategoriesSmokeTest(passedDb, options = {}) {
  const quickVariations = CATEGORY_TEST_VARIATIONS.filter(v =>
    v.id === 'PC1' || v.id === 'PC2'
  );

  return runProductCategoriesDualWriteTests(passedDb, {
    ...options,
    variations: quickVariations,
  });
}

export default {
  runProductCategoriesDualWriteTests,
  generateTestCategory,
  CATEGORY_TEST_VARIATIONS,
};
